/**
 * Geocoding & Timezone resolution module.
 *
 * 1. Nominatim (OpenStreetMap) → city name to lat/lon
 * 2. TimeAPI.io → lat/lon to IANA timezone
 * 3. Intl.DateTimeFormat → IANA timezone + date → exact UTC offset (handles DST)
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const TIMEZONE_URL = 'https://timeapi.io/api/timezone/coordinate';

/**
 * Search for cities matching a query string.
 * Returns array of { displayName, lat, lon, country }
 */
export async function searchCity(query) {
  if (!query || query.trim().length < 2) return [];

  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: '6',
    'accept-language': 'de',
    addressdetails: '1'
  });

  const resp = await fetch(`${NOMINATIM_URL}?${params}`, {
    headers: { 'User-Agent': 'HDCalculator/1.0 (hd.boederbrothers.com)' }
  });

  if (!resp.ok) throw new Error('Geocoding fehlgeschlagen');

  const data = await resp.json();

  return data.map(r => ({
    displayName: r.display_name,
    lat: parseFloat(r.lat),
    lon: parseFloat(r.lon),
    country: r.address?.country || '',
    countryCode: r.address?.country_code || '',
    city: r.address?.city || r.address?.town || r.address?.village || r.name || query
  }));
}

/**
 * Get IANA timezone name from coordinates.
 * Uses TimeAPI.io (free, no key required).
 */
export async function getTimezoneFromCoords(lat, lon) {
  const params = new URLSearchParams({
    latitude: lat.toString(),
    longitude: lon.toString()
  });

  const resp = await fetch(`${TIMEZONE_URL}?${params}`);

  if (!resp.ok) {
    // Fallback: estimate timezone from longitude
    console.warn('Timezone API failed, falling back to longitude estimate');
    return estimateTimezoneFromLongitude(lon);
  }

  const data = await resp.json();
  return data.timeZone || estimateTimezoneFromLongitude(lon);
}

/**
 * Calculate the exact UTC offset (in hours) for a specific date in a given IANA timezone.
 * This correctly handles DST transitions for any historical date.
 *
 * @param {string} dateStr - "YYYY-MM-DD"
 * @param {string} timeStr - "HH:MM"
 * @param {string} ianaTimezone - e.g. "Europe/Berlin"
 * @returns {number} UTC offset in hours (e.g. 1 for CET, 2 for CEST)
 */
export function getUtcOffsetForDate(dateStr, timeStr, ianaTimezone) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Create a date in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

  // Get the local time representation in the target timezone
  const localStr = utcDate.toLocaleString('en-US', { timeZone: ianaTimezone });
  const utcStr = utcDate.toLocaleString('en-US', { timeZone: 'UTC' });

  const localDate = new Date(localStr);
  const utcParsed = new Date(utcStr);

  // Difference = UTC offset
  const offsetMs = localDate - utcParsed;
  return offsetMs / 3600000;
}

/**
 * Get a human-readable timezone description for the given date.
 * e.g. "MEZ (UTC+1)" or "MESZ (UTC+2)"
 */
export function getTimezoneDescription(dateStr, timeStr, ianaTimezone) {
  const offset = getUtcOffsetForDate(dateStr, timeStr, ianaTimezone);
  const sign = offset >= 0 ? '+' : '';

  // Try to get the short timezone name
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hours - offset, minutes, 0));

  let shortName = '';
  try {
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: ianaTimezone,
      timeZoneName: 'short'
    }).formatToParts(date);
    shortName = parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { /* ignore */ }

  const offsetStr = `UTC${sign}${offset}`;

  if (shortName && shortName !== offsetStr) {
    return `${shortName} (${offsetStr})`;
  }
  return offsetStr;
}

/**
 * Rough timezone estimate from longitude (fallback only).
 * Maps to the nearest standard IANA timezone.
 */
function estimateTimezoneFromLongitude(lon) {
  const offsetHours = Math.round(lon / 15);

  // Map common offsets to IANA zones
  const zoneMap = {
    '-12': 'Etc/GMT+12', '-11': 'Pacific/Midway', '-10': 'Pacific/Honolulu',
    '-9': 'America/Anchorage', '-8': 'America/Los_Angeles', '-7': 'America/Denver',
    '-6': 'America/Chicago', '-5': 'America/New_York', '-4': 'America/Halifax',
    '-3': 'America/Sao_Paulo', '-2': 'Atlantic/South_Georgia', '-1': 'Atlantic/Azores',
    '0': 'Europe/London', '1': 'Europe/Berlin', '2': 'Europe/Helsinki',
    '3': 'Europe/Moscow', '4': 'Asia/Dubai', '5': 'Asia/Karachi',
    '6': 'Asia/Almaty', '7': 'Asia/Bangkok', '8': 'Asia/Shanghai',
    '9': 'Asia/Tokyo', '10': 'Australia/Sydney', '11': 'Pacific/Noumea',
    '12': 'Pacific/Auckland'
  };

  return zoneMap[offsetHours.toString()] || `Etc/GMT${offsetHours >= 0 ? '-' : '+'}${Math.abs(offsetHours)}`;
}
